import {
  DataGridLayout,
  DateRangeFilter,
  Filters,
  FiltersLayout,
  List,
  ListItem,
  RelationListLayout,
} from 'framework/list';
import { BadgeField, DateField, ListColumnTotalField, PercentField, SearchLinkField } from 'framework/ui/field';
import {
  ACTIVE_STATUS,
  AssignmentStatusFilter,
  AssignmentTargetLinkField,
  BenchBadge,
  getMaxPercentLabel,
  getMinPercentLabel,
  PositionInAssignmentField,
  useAssignmentService,
  useAssignmentUrl,
} from 'domain/shared/assignment';
import { EmployeeLinkField, EmployeePositionFilter, EmployeeFilter } from 'domain/shared/employee';
import { ResourceData } from 'framework/crud';
import CreateEmployeeAssignmentButton from 'domain/people/employee/assignments/List/button/CreateEmployeeAssignmentButton';
import EditEmployeeAssignmentButton from 'domain/people/employee/assignments/List/button/EditEmployeeAssignmentButton';
import CopyEmployeeAssignmentButton from 'domain/people/employee/assignments/List/button/CopyEmployeeAssignmentButton';
import { useEmployeeId } from 'domain/people/employee/employeeIdentityRouting';
import { ProjectFilter } from 'domain/shared/project';

const EmployeeAssignmentsList = () => {
  const { search } = useAssignmentService();
  const employeeId = useEmployeeId();

  const getDataMethod = async (params: ResourceData) => {
    return search({
      ...params,
      filter: {
        'specification:assignmentsByEmployeeAlias': { eq: employeeId },
        ...params.filter,
      },
    });
  };

  const { getSingleEntityUrl } = useAssignmentUrl();

  const filters = (
    <Filters>
      <FiltersLayout>
        <AssignmentStatusFilter source="status" label="Status" />
        <EmployeeFilter source="owner.alias" label="Owner" />
        <EmployeeFilter source="createdBy.alias" label="Created by" />
        <EmployeePositionFilter source="specification:assignmentsByPositionIds" label="Position" />
        <ProjectFilter source="project.id" label="Project" />
        <DateRangeFilter canBeInFuture source="from" label="Start date" />
        <DateRangeFilter canBeInFuture source="to" label="End date" />
      </FiltersLayout>
    </Filters>
  );

  return (
    <List
      getDataMethod={getDataMethod}
      defaultFilter={{
        status: { in: [ACTIVE_STATUS] },
      }}
    >
      <RelationListLayout
        filter={filters}
        header="Assignments"
        headerVariant="h4"
        actions={[<CreateEmployeeAssignmentButton />]}
      >
        <DataGridLayout
          buttons={[
            <CopyEmployeeAssignmentButton source="id" size="small" />,
            <EditEmployeeAssignmentButton
              size="small"
              isVisible={(data: ListItem) => {
                return data.canEdit;
              }}
            />,
          ]}
          isSmallTable
          showTotal={false}
        >
          <BadgeField
            badges={[<BenchBadge isVisible={assignment => assignment.bench} />]}
            source="id"
            label="ID"
            isSortable
            style={{ display: 'inline-flex' }}
          >
            <SearchLinkField url={getSingleEntityUrl} width={70} />
          </BadgeField>
          <AssignmentTargetLinkField
            label="Target"
            source="target.id"
            nameSource="target.displayName"
            typeSource="target.type"
            serviceCatalogIdSource="target.targetId"
          />
          <EmployeeLinkField source="ownerId" nameSource="ownerName" label="Owner" isSortable />
          <PositionInAssignmentField label="Position" source="assignmentType" />
          <DateField source="from" label="Start date" isSortable />
          <DateField source="to" label="End date" isSortable />
          <PercentField
            source="minPercent"
            label={<ListColumnTotalField source="minPercent" value={getMinPercentLabel} />}
            isSortable
          />
          <PercentField
            source="maxPercent"
            label={<ListColumnTotalField source="maxPercent" value={getMaxPercentLabel} />}
            isSortable
          />
        </DataGridLayout>
      </RelationListLayout>
    </List>
  );
};

export default EmployeeAssignmentsList;
